import { FC, useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
  Box,
  IconButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useFetchAllObjectQuery } from '@redux/api/objects.api'
import { InternalIdInput } from '@library'
import { Variable } from '@mobileTypes'

interface VariableCreateDialogProps {
  open: boolean
  mode?: 'create' | 'edit'
  title?: string
  description?: string
  infoMessage?: React.ReactNode
  initialData?: Variable
  existingVariables: Array<{ internalId: string; isGlobal?: boolean }>
  defaultScope?: 'local' | 'global'
  defaultVariableType?: 'single' | 'multiple'
  defaultValueType?: string
  valueTypeOptions?: Array<{ value: string; label: string }>
  showValueTypeSelector?: boolean
  showScopeSelector?: boolean
  showVariableTypeSelector?: boolean
  showDefaultValue?: boolean
  showObjectCodeSelector?: boolean
  showPersistentToggle?: boolean
  onSave: (variable: Variable, isGlobal: boolean) => void
  onClose: () => void
}

const DEFAULT_VALUE_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object', label: 'Object' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'DateTime' },
]

export const VariableCreateDialog: FC<VariableCreateDialogProps> = ({
  open,
  mode = 'create',
  title,
  description,
  infoMessage,
  initialData,
  existingVariables,
  defaultScope = 'local',
  defaultVariableType = 'single',
  defaultValueType = 'string',
  valueTypeOptions = DEFAULT_VALUE_TYPES,
  showScopeSelector = false,
  showValueTypeSelector = false,
  showVariableTypeSelector = false,
  showDefaultValue = false,
  showObjectCodeSelector = false,
  showPersistentToggle = false,
  onSave,
  onClose,
}) => {
  const [variableInternalId, setVariableInternalId] = useState('')
  const [scope, setScope] = useState<'local' | 'global'>(defaultScope)
  const [variableType, setVariableType] = useState<'single' | 'multiple'>(defaultVariableType)
  const [valueType, setValueType] = useState<string>(defaultValueType)
  const [defaultValue, setDefaultValue] = useState<any>(undefined)
  const [objectCode, setObjectCode] = useState<string | undefined>(undefined)
  const [persistent, setPersistent] = useState(false)
  const [internalIdError, setInternalIdError] = useState('')

  // Fetch all objects for objectCode selection
  const { data: allObjectsData, isLoading: isLoadingObjects } = useFetchAllObjectQuery(undefined, {
    skip: !showObjectCodeSelector,
  })

  const objectOptions = useMemo(() => {
    if (!allObjectsData) return []
    return allObjectsData
  }, [allObjectsData])

  // Compute selected object for Autocomplete
  const selectedObject = useMemo(() => {
    if (!objectCode || objectOptions.length === 0) return null
    return objectOptions.find(o => o.code === objectCode) || null
  }, [objectCode, objectOptions])

  // Compute dialog title
  const dialogTitle = title || (mode === 'edit' ? 'Edit Variable' : 'Create Variable')

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        setVariableInternalId(initialData.internalId ?? '')
        setVariableType(initialData.variableType || 'single')
        setValueType(initialData.valueType || 'string')
        setDefaultValue(initialData.defaultValue)
        setObjectCode(initialData.objectCode)
        setPersistent(Boolean(initialData.persistent))
        setScope('local') // Edit mode doesn't change scope
      } else {
        setVariableInternalId('')
        setScope(defaultScope)
        setVariableType(defaultVariableType)
        setValueType(defaultValueType)
        setDefaultValue(undefined)
        setObjectCode(undefined)
        setPersistent(false)
      }
      setInternalIdError('')
    }
  }, [open, mode, initialData, defaultScope, defaultValueType, defaultVariableType])

  const handleSave = () => {
    if (!variableInternalId.trim()) {
      setInternalIdError('Internal ID is required')
      return
    }

    const trimmed = variableInternalId.trim()

    // Check for duplicate internalId (excluding current variable in edit mode)
    const existingVariable = existingVariables.find(v =>
      v.internalId === trimmed && (mode === 'create' || v.internalId !== initialData?.internalId)
    )
    if (existingVariable) {
      setInternalIdError('A variable with this Internal ID already exists')
      return
    }

    // Build the full variable object
    const variable: Variable = {
      internalId: trimmed,
      variableType: variableType,
      valueType: valueType,
      defaultValue: defaultValue,
      objectCode: valueType === 'object' ? objectCode : undefined,
      persistent: showPersistentToggle ? persistent : undefined,
    }

    onSave(variable, scope === 'global')
    onClose()
  }

  const handleDefaultValueChange = (newValue: any) => {
    setDefaultValue(newValue)
  }

  const renderDefaultValueInput = () => {
    if (!showDefaultValue) return null

    if (variableType === 'multiple') {
      // Multiple - Array of elements with Add button
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Default Value (Array)
            </Typography>
            <Button
              size='small'
              startIcon={<AddIcon />}
              onClick={() => {
                const currentArray = Array.isArray(defaultValue) ? defaultValue : []
                const newItemValue = valueType === 'boolean' ? false : valueType === 'number' ? 0 : ''
                handleDefaultValueChange([...currentArray, newItemValue])
              }}
            >
              Add
            </Button>
          </Box>
          {Array.isArray(defaultValue) && defaultValue.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {defaultValue.map((item: any, index: number) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    {valueType === 'boolean' ? (
                      <FormControl fullWidth size='small'>
                        <InputLabel>Value {index + 1}</InputLabel>
                        <Select
                          value={item === undefined || item === null ? '' : String(item)}
                          onChange={e => {
                            const newArray = [...defaultValue]
                            const val = e.target.value
                            if (val === '') {
                              newArray[index] = undefined
                            } else {
                              newArray[index] = val === 'true'
                            }
                            handleDefaultValueChange(newArray)
                          }}
                          label={`Value ${index + 1}`}
                        >
                          <MenuItem value=''>None</MenuItem>
                          <MenuItem value='true'>true</MenuItem>
                          <MenuItem value='false'>false</MenuItem>
                        </Select>
                      </FormControl>
                    ) : valueType === 'number' ? (
                      <TextField
                        fullWidth
                        size='small'
                        type='number'
                        label={`Value ${index + 1}`}
                        value={item}
                        onChange={e => {
                          const newArray = [...defaultValue]
                          const num = parseFloat(e.target.value)
                          newArray[index] = isNaN(num) ? 0 : num
                          handleDefaultValueChange(newArray)
                        }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        size='small'
                        label={`Value ${index + 1}`}
                        value={item}
                        onChange={e => {
                          const newArray = [...defaultValue]
                          newArray[index] = e.target.value
                          handleDefaultValueChange(newArray)
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    size='small'
                    color='error'
                    onClick={() => {
                      const newArray = [...defaultValue]
                      newArray.splice(index, 1)
                      handleDefaultValueChange(newArray.length > 0 ? newArray : undefined)
                    }}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', py: 1 }}>
              No items. Click "Add" to add an element to the array.
            </Typography>
          )}
        </Box>
      )
    }

    // Single value
    if (valueType === 'boolean') {
      return (
        <FormControl fullWidth>
          <InputLabel>Default Value</InputLabel>
          <Select
            value={defaultValue === undefined ? '' : String(defaultValue)}
            onChange={e => {
              const val = e.target.value
              if (val === '') {
                handleDefaultValueChange(undefined)
              } else {
                handleDefaultValueChange(val === 'true')
              }
            }}
            label='Default Value'
          >
            <MenuItem value=''>None</MenuItem>
            <MenuItem value='true'>true</MenuItem>
            <MenuItem value='false'>false</MenuItem>
          </Select>
        </FormControl>
      )
    }

    if (valueType === 'number') {
      return (
        <TextField
          fullWidth
          type='number'
          label='Default Value'
          value={defaultValue !== undefined ? defaultValue : ''}
          onChange={e => {
            const val = e.target.value
            if (val === '') {
              handleDefaultValueChange(undefined)
            } else {
              const num = parseFloat(val)
              handleDefaultValueChange(isNaN(num) ? undefined : num)
            }
          }}
        />
      )
    }

    // String or other types
    return (
      <TextField
        fullWidth
        label='Default Value'
        value={defaultValue !== undefined ? String(defaultValue) : ''}
        onChange={e => {
          const val = e.target.value
          if (val === '') {
            handleDefaultValueChange(undefined)
          } else {
            handleDefaultValueChange(val)
          }
        }}
      />
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {description && (
            <Typography variant='body2' color='text.secondary'>
              {description}
            </Typography>
          )}

          <InternalIdInput
            value={variableInternalId}
            onChange={(v) => {
              setVariableInternalId(v)
              setInternalIdError('')
            }}
            fullWidth
            label='Internal ID'
            required
            error={!!internalIdError}
            helperText={internalIdError || 'Unique ID to reference this variable'}
            autoFocus
          />

          {showScopeSelector && <FormControl component='fieldset'>
            <FormLabel component='legend' sx={{ fontSize: '0.875rem', mb: 1 }}>
              Variable Scope
            </FormLabel>
            <RadioGroup
              row
              value={scope}
              onChange={(e) => setScope(e.target.value as 'local' | 'global')}
            >
              <FormControlLabel
                value='local'
                control={<Radio size='small' />}
                label='Local Variable'
              />
              <FormControlLabel
                value='global'
                control={<Radio size='small' />}
                label='Global Variable'
              />
            </RadioGroup>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
              {scope === 'local'
                ? 'Variable will be available only in this form/list item'
                : 'Variable will be available across all forms and list items'}
            </Typography>
          </FormControl>}

          {showVariableTypeSelector && (
            <FormControl component='fieldset'>
              <FormLabel component='legend' sx={{ fontSize: '0.875rem', mb: 1 }}>
                Variable Type
              </FormLabel>
              <RadioGroup
                row
                value={variableType}
                onChange={(e) => {
                  const newType = e.target.value as 'single' | 'multiple'
                  setVariableType(newType)
                  // Reset default value when switching types
                  if (newType === 'multiple' && defaultValue !== undefined && !Array.isArray(defaultValue)) {
                    setDefaultValue([defaultValue])
                  } else if (newType === 'single' && Array.isArray(defaultValue)) {
                    setDefaultValue(defaultValue.length > 0 ? defaultValue[0] : undefined)
                  }
                }}
              >
                <FormControlLabel
                  value='single'
                  control={<Radio size='small' />}
                  label='Single Variable'
                />
                <FormControlLabel
                  value='multiple'
                  control={<Radio size='small' />}
                  label='Multiple Variables'
                />
              </RadioGroup>
            </FormControl>
          )}

          {showPersistentToggle && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={persistent}
                    onChange={(_, checked) => setPersistent(checked)}
                  />
                }
                label='Persist between app sessions'
              />
              <Typography variant='caption' color='text.secondary'>
                If enabled, the variable value will be kept after app restart.
              </Typography>
            </Box>
          )}

          {showValueTypeSelector && (
            <FormControl fullWidth>
              <InputLabel>Value Type</InputLabel>
              <Select
                value={valueType}
                onChange={(e) => {
                  const newType = e.target.value
                  setValueType(newType)
                  // Reset default value when changing value type
                  setDefaultValue(undefined)
                  // Clear objectCode if switching away from 'object' type
                  if (newType !== 'object') {
                    setObjectCode(undefined)
                  }
                }}
                label='Value Type'
              >
                {valueTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                The type of value this variable will store
              </Typography>
            </FormControl>
          )}

          {showObjectCodeSelector && valueType === 'object' && (
            <Autocomplete
              options={objectOptions}
              getOptionLabel={option => option?.title || ''}
              value={selectedObject}
              isOptionEqualToValue={(option, value) => option?.code === value?.code}
              onChange={(_, newValue) => {
                setObjectCode(newValue?.code || undefined)
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Object Code'
                  helperText={isLoadingObjects && objectCode && !selectedObject ? `Loading: ${objectCode}` : undefined}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingObjects && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              loading={isLoadingObjects}
            />
          )}

          {renderDefaultValueInput()}

          {infoMessage && (
            <Box sx={{
              p: 1.5,
              bgcolor: 'info.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main',
            }}>
              {infoMessage}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant='contained' disabled={!variableInternalId.trim()}>
          {mode === 'edit' ? 'Save' : 'Create Variable'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
